import Icon from '@dc/ui-library/src/molecules/Icon/Icon';
import React, { Fragment, PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { resolveByCountry } from '@dc/ui-library/src/helpers/environment';
import Button from '@dc/ui-library/src/molecules/Button/Button';
import FormItem from '@dc/ui-library/src/organisms/FormItem/FormItem';
import Filter from './Filter';
import { filterTypes, touchDeviceType } from './filterHelpers';
import {
  setPriceRangeFrom,
  setPriceRangeTo,
  resetFilterOptions
} from '../../../../../appState/ducks/listingPageFilters';
import { fetchListingPageData } from '../../../../../appState/ducks/network';
import { betweenPropShape, filterOptionPropTypes } from './propTypes';

class FilterPriceRange extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showSpinner: false
    };

    this.onUpdatePriceRangeFrom = this.onUpdatePriceRangeFrom.bind(this);
    this.onUpdatePriceRangeTo = this.onUpdatePriceRangeTo.bind(this);
    this.applyPriceRange = this.applyPriceRange.bind(this);
    this.getPriceRangeFrom = this.getPriceRangeFrom.bind(this);
    this.getPriceRangeTo = this.getPriceRangeTo.bind(this);
    this.resetPriceInputs = this.resetPriceInputs.bind(this);
    this.onPriceRangeFormSubmit = this.onPriceRangeFormSubmit.bind(this);
    this.priceFromInput = React.createRef();
    this.priceToInput = React.createRef();
    this.initialPriceFrom = this.props.priceFrom;
    this.initialPriceTo = this.props.priceTo;
  }

  componentDidUpdate() {
    if (!this.props.pendingProductsRequest) {
      this.setState({ showSpinner: false });  // eslint-disable-line
    }
  }

  onUpdatePriceRangeFrom(priceFrom) {
    this.resetFilterActiveOptions();
    this.fixMinAndMaxPrices(priceFrom, this.getPriceRangeTo());

    this.props.setPriceRangeFrom({
      filterName: this.props.name,
      value: priceFrom === '' ? '' : parseInt(priceFrom, 10)
    });
  }

  onUpdatePriceRangeTo(priceTo) {
    this.resetFilterActiveOptions();

    if (!this.props.priceFrom) {
      this.props.setPriceRangeFrom({
        filterName: this.props.name,
        value: 0
      });
    }

    this.props.setPriceRangeTo({
      filterName: this.props.name,
      value: priceTo === '' ? '' : parseInt(priceTo, 10)
    });
  }

  onPriceRangeFormSubmit(e) {
    if (e) {
      e.preventDefault();
    }
    this.applyPriceRange();
  }

  getPriceRangeFrom() {
    return this.props.currentFormBetween.left.value;
  }

  getPriceRangeTo() {
    return this.props.currentFormBetween.right.value;
  }

  fixMinAndMaxPrices(priceFrom, priceTo) {
    if (priceFrom !== '' && parseInt(priceFrom, 10) > parseInt(priceTo, 10)) {
      this.props.setPriceRangeTo({
        filterName: this.props.name,
        value: parseInt(priceFrom, 10)
      });
    }
  }

  hasFilterActiveOptions() {
    return this.props.options.find((option) => option.active);
  }

  applyPriceRange() {
    this.setState({ showSpinner: true });
    this.fixMinAndMaxPrices(this.getPriceRangeFrom(), this.getPriceRangeTo());
    const whatChanged = 'filters';
    this.props.fetchListingPageData(whatChanged);
  }

  resetFilterActiveOptions() {
    if (this.hasFilterActiveOptions()) {
      this.props.resetFilterOptions({
        filterName: this.props.name,
        clearBetweenValues: false,
        fetchNewData: false
      });
    }
  }

  resetPriceInputs() {
    this.onUpdatePriceRangeFrom(this.initialPriceFrom);
    this.onUpdatePriceRangeTo(this.initialPriceTo);
  }

  renderPriceInputs() {
    return (
      <>
        <div className={'dc-filter__price-range'}>
          <span>
            {resolveByCountry('£', '€')}
          </span>

          <form onSubmit={this.onPriceRangeFormSubmit}>
            <FormItem
              ref={this.priceFromInput}
              name={'priceFromInput'}
              type={'number'}
              label={'From'}
              value={this.getPriceRangeFrom()}
              onValueUpdateImmediate={(value) => {
                this.onUpdatePriceRangeFrom(value);
              }}
              allowExternalValueChanges
            />

            <FormItem
              ref={this.priceToInput}
              name={'priceToInput'}
              type={'number'}
              label={'To'}
              value={this.getPriceRangeTo()}
              onValueUpdateImmediate={(value) => {
                this.onUpdatePriceRangeTo(value);
              }}
              allowExternalValueChanges
            />
            <input
              type={'submit'}
              className={'dc-filter__price-range-submit'}
              name={'price_range_submit'}
            />
          </form>

          {!touchDeviceType(this.props.deviceType) &&
            <Button style={'small-outline'} onClick={this.applyPriceRange}>Refresh</Button>}
        </div>
      </>
    );
  }

  render() {
    return (
      <Filter resetPriceInputs={this.resetPriceInputs} {...this.props}>
        {this.state.showSpinner && (
          <Icon
            icon={'Spinner'}
            size={'large'}
            style={{
              position: 'absolute',
              zIndex: 4,
              margin: '-20% calc(50% - 20px)',
              width: '40px',
              height: '40px'
            }}
            spin
          />
        )}
        {this.renderPriceInputs()}
      </Filter>
    );
  }
}

FilterPriceRange.displayName = 'FilterPriceRange';

FilterPriceRange.propTypes = {
  priceFrom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  priceTo: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.oneOf(Object.values(filterTypes)).isRequired,
  deviceType: PropTypes.oneOf(['wide', 'desktop', 'tablet', 'mobile']).isRequired,
  options: PropTypes.arrayOf(filterOptionPropTypes).isRequired,
  currentFormBetween: betweenPropShape.isRequired,
  setPriceRangeTo: PropTypes.func.isRequired,
  setPriceRangeFrom: PropTypes.func.isRequired,
  resetFilterOptions: PropTypes.func.isRequired,
  fetchListingPageData: PropTypes.func.isRequired,
  pendingProductsRequest: PropTypes.bool
};

FilterPriceRange.defaultProps = {
  pendingProductsRequest: false
};

function mapStateToProps(state) {
  return {
    deviceType: state.general.device.deviceType,
    priceFrom: state.listingPage.filters.list.find(
      (filterItem) => filterItem.name === filterTypes.price
    ).currentFormBetween.left.value,
    priceTo: state.listingPage.filters.list.find(
      (filterItem) => filterItem.name === filterTypes.price
    ).currentFormBetween.right.value,
    pendingProductsRequest: state.listingPage.pageSetup.xhrStatus.pendingProductsRequest
  };
}

const dispatchFunctions = {
  setPriceRangeFrom,
  setPriceRangeTo,
  resetFilterOptions,
  fetchListingPageData
};

export default connect(mapStateToProps, dispatchFunctions)(FilterPriceRange);
